import { useBrand } from "@branding/useBrand";

/**
 * Privacy Policy. Grounded in how the product actually handles data (server-
 * backed, tenant-scoped, RBAC enforced server-side, HttpOnly session cookies,
 * strict same-origin CSP, and no third-party client-side trackers). Written as
 * a template the operator adapts to its own entity and jurisdiction.
 */
export function Privacy() {
  const brand = useBrand();

  return (
    <section className="mk-section narrow mk-prose">
      <div className="mk-eyebrow">Legal</div>
      <h1 className="mk-h1">Privacy Policy</h1>
      <p className="mk-updated">Last updated: 21 July 2026</p>

      <p>
        This policy explains what data {brand.name} handles and how. It is a
        plain-language template; the operator of this deployment should adapt it
        to its own entity and applicable law (including UAE data-protection law).
        It is not legal advice.
      </p>

      <h2>Data we handle</h2>
      <ul>
        <li>
          <strong>Account data</strong> — the name and email of each user, and
          the role they hold in your organization, used to authenticate and to
          enforce access.
        </li>
        <li>
          <strong>Organization &amp; ledger data</strong> — the entities,
          accounts, journal entries, settings, and reconciliations you enter. This
          is your financial record; we process it to provide the Service.
        </li>
        <li>
          <strong>Operational data</strong> — the minimum needed to run and secure
          the Service, such as session records tied to your sign-in.
        </li>
      </ul>

      <h2>How your data is protected</h2>
      <ul>
        <li>
          <strong>Tenant isolation.</strong> Your data is scoped to your
          organization and this separation is enforced on the server — members of
          one organization cannot read another organization’s data.
        </li>
        <li>
          <strong>Authentication &amp; roles.</strong> Access requires signing in,
          and role-based permissions (owner, admin, member, viewer) are enforced
          server-side, not just hidden in the interface.
        </li>
        <li>
          <strong>Sessions.</strong> Your session is carried in an HttpOnly cookie
          that scripts in the browser cannot read, reducing the risk of session
          theft.
        </li>
        <li>
          <strong>Strict content-security policy.</strong> The application is
          restricted to its own origin — it does not load third-party scripts and
          cannot send your ledger data to another origin.
        </li>
      </ul>

      <h2>Third parties and tracking</h2>
      <p>
        {brand.name} does not embed third-party advertising or analytics trackers
        in the application, and does not sell your data. The Service is operated on
        cloud infrastructure that hosts the application and database on the
        operator’s behalf; those providers process data only to run the Service.
      </p>

      <h2>Data retention and export</h2>
      <p>
        Your organization’s data is retained while your account is active. You can
        export your data at any time using the built-in CSV export, and you can
        request deletion of your organization’s data. Some records may be retained
        where required by law.
      </p>

      <h2>Your choices</h2>
      <ul>
        <li>Access and export your financial data from inside the app.</li>
        <li>Manage who has access, and at what role, from your organization’s settings.</li>
        <li>Request correction or deletion of your account and organization data.</li>
      </ul>

      <h2>Cookies</h2>
      <p>
        We use a strictly necessary session cookie to keep you signed in. We do
        not use cookies for advertising or cross-site tracking.
      </p>

      <h2>Contact</h2>
      <p>
        For privacy questions or requests, contact the operator of your
        {" "}{brand.name} deployment, or reach us from inside the app.
      </p>
    </section>
  );
}
